import type { Experiment, Variant } from 'types';

/** Stands in for a variant key that has not been set yet, so the snippet still reads as code. */
const KEY_PLACEHOLDER = '<variant key>';

/**
 * Never a real variant ID: pasted as it is, a real one would pin every visitor to that
 * variant, since the runtime accepts any seeded assignment.
 */
export const SEED_VARIANT_PLACEHOLDER = '<variantId from the decision>';

type SnippetParams = {
  experiment: Pick<Experiment, 'id' | 'websiteId' | 'flagKey' | 'bucketOn'>;
  variants: readonly Pick<Variant, 'id' | 'key'>[];
  /** The tracking server's origin, from config.TRACKING_URL. */
  trackingUrl: string;
};

const flagKeyOf = (experiment: SnippetParams['experiment']) => experiment.flagKey || 'your-flag-key';

/** The identity argument for the bucketing choice: a customer ID experiment ignores visitor IDs. */
const identityOf = (experiment: SnippetParams['experiment']) =>
  experiment.bucketOn === 'customer_id' ? '{ id: customer.id, attributes }' : '{ visitorId, attributes }';

/** The SDK call with this experiment's real flag key and variant keys. */
export function sdkSnippet({ experiment, variants }: SnippetParams): string {
  const cases = variants
    .map((variant) => `  case '${variant.key || KEY_PLACEHOLDER}':\n    // render variant ${variant.id}\n    break;`)
    .join('\n');
  const isCustomerId = experiment.bucketOn === 'customer_id';
  const visitorLine = isCustomerId
    ? ''
    : [
        '// The visitor ID from your session, or a new one. Store a new one and send it back',
        '// on every request: a different ID is a different visitor.',
        'const visitorId = session.visitorId ?? pertento.createVisitorId();',
        'session.visitorId = visitorId;',
        '',
      ].join('\n');
  const seedVisitor = isCustomerId ? '' : ', visitorId';
  const trackIdentity = isCustomerId ? 'id: customer.id' : 'visitorId';

  return `import { createPertento } from '@pertentoai/sdk';

const pertento = createPertento({
  websiteId: ${experiment.websiteId},
  serverKey: process.env.PERTENTO_SERVER_KEY,
});
await pertento.ready();

${visitorLine}const decision = await pertento.getVariant('${flagKeyOf(experiment)}', ${identityOf(experiment)});

switch (decision.variantKey) {
${cases}
  default:
    // Not enrolled (decision.reason says why): render what you show today.
}

// In the page's <head>, before the Pertento script:
const seed = pertento.seedScript([decision]${seedVisitor});

// Later, when the visitor converts. keys lists the flags they were decided on.
await pertento.track('purchase', { keys: ['${flagKeyOf(experiment)}'], ${trackIdentity}, data: { revenue: 4900 } });`;
}

/** What seedScript() writes, for a stack that builds the page without the SDK. */
export function seedSnippet({ experiment }: SnippetParams): string {
  const assignment =
    experiment.bucketOn === 'customer_id'
      ? `{ "variantId": "${SEED_VARIANT_PLACEHOLDER}", "enrolAs": "<enrolAs from the decision>" }`
      : `{ "variantId": "${SEED_VARIANT_PLACEHOLDER}" }`;
  const visitor = experiment.bucketOn === 'customer_id' ? '' : '"visitorId": "<visitor id>", ';

  return `<script>window.PertentoSeed = { ${visitor}"assignments": { "${experiment.id}": ${assignment} } };</script>`;
}

/**
 * POST /sdk/decide by hand, then POST /sdk/events for a conversion. /sdk/events requires
 * `keys`: the flags the person was decided on.
 */
export function curlSnippet({ experiment, trackingUrl }: SnippetParams): string {
  const identity = experiment.bucketOn === 'customer_id' ? '"id": "<customer id>"' : '"visitorId": "<visitor id>"';
  const keys = `"keys": ["${flagKeyOf(experiment)}"]`;

  return `curl -X POST ${trackingUrl}/sdk/decide \\
  -H "Authorization: Bearer $PERTENTO_SERVER_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ ${keys}, ${identity}, "attributes": {} }'

curl -X POST ${trackingUrl}/sdk/events \\
  -H "Authorization: Bearer $PERTENTO_SERVER_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ ${keys}, ${identity}, "events": [{ "name": "purchase", "data": { "revenue": 4900 } }] }'`;
}

type BrowserSnippetParams = {
  experiment: Pick<Experiment, 'id'>;
  variants: readonly Pick<Variant, 'id' | 'name'>[];
};

const variantLabel = (variant: Pick<Variant, 'id' | 'name'>) => variant.name ?? `Variant ${variant.id}`;

/**
 * For browser assignment: how the page's own code shows the variant the snippet picked. The
 * variant's own JavaScript runs when it applies, so a call from there needs no timing; the
 * assignment in window.Pertento.expvar is only certain once the snippet has run, so it is shown
 * for later moments (a click, a route change), not for the first render.
 */
export function browserSnippet({ experiment, variants }: BrowserSnippetParams): string {
  const perVariant = variants
    .map((variant) => `//   ${variantLabel(variant)}: showVersion('${variant.id}');`)
    .join('\n');
  const cases = variants.map((variant) => `  case '${variant.id}': // ${variantLabel(variant)}\n    break;`).join('\n');

  return `// 1. In each variant's JavaScript (Open Editor), call your own code:
${perVariant}

// 2. Later, on a click or a route change, read the assignment:
const variantId = window.Pertento?.expvar?.['${experiment.id}'];
switch (variantId) {
${cases}
  default:
    // Not in the experiment: show what you show today.
}`;
}
